import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useWorkspace } from '../context/WorkspaceContext';
import { useBetaAccess } from '../hooks/useBetaAccess';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '../services/stripeClient';
import { supabase } from '../services/supabaseClient';
import Navbar from '../components/Navbar';
import { CheckIcon, XMarkIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { PRICING } from '../config/pricing';

interface PricingTier {
  name: string;
  price: number;
  annualPrice: number;
  annualMonthlyEquivalent?: number;
  discountedPrice?: number;
  features: string[];
  limitations: string[];
  ctaText: string;
  ctaAction: 'select' | 'contact';
  badge?: string;
}

const pricingTiers: { [key: string]: PricingTier} = {
  starter: {
    name: 'Starter',
    price: PRICING.STARTER.MONTHLY,
    annualPrice: PRICING.STARTER.ANNUAL,
    annualMonthlyEquivalent: Math.round(PRICING.STARTER.MONTHLY_EQUIVALENT_ANNUAL),
    features: [
      'Unlimited users',
      '1 org (production or sandbox)',
      'FlowDocs Performance Dashboard',
      'Scorecards for every flow',
      'AI-generated summaries',
      'Field impact tracking',
      'Flow trigger matrix',
      'Flows updated daily'
    ],
    limitations: [],
    ctaText: 'Select Starter',
    ctaAction: 'select',
    badge: 'Basic'
  },
  standard: {
    name: 'Standard',
    price: PRICING.STANDARD.MONTHLY,
    annualPrice: PRICING.STANDARD.ANNUAL,
    annualMonthlyEquivalent: Math.round(PRICING.STANDARD.MONTHLY_EQUIVALENT_ANNUAL),
    features: [
      'Unlimited users',
      '1 environment (up to 5 orgs per environment)',
      'FlowDocs Performance Dashboard',
      'All core documentation features',
      'Scorecards for every flow',
      'Field impact tracking',
      'Flow trigger matrix',
      'Flows updated daily'
    ],
    limitations: [],
    ctaText: 'Select Standard',
    ctaAction: 'select',
    badge: 'Value'
  },
  professional: {
    name: 'Professional',
    price: PRICING.PROFESSIONAL.MONTHLY,
    annualPrice: PRICING.PROFESSIONAL.ANNUAL,
    annualMonthlyEquivalent: Math.round(PRICING.PROFESSIONAL.MONTHLY_EQUIVALENT_ANNUAL),
    features: [
      'Everything in Starter',
      '1 environment (1 production org + up to 4 related sandboxes)',
      'Unlock additional environments (add-on)',
      'Export Flow Summaries',
      'On-demand Flow Syncs',
      'Priority support'
    ],
    limitations: [],
    ctaText: 'Select Professional',
    ctaAction: 'select',
    badge: 'Recommended'
  },
  enterprise: {
    name: 'Enterprise',
    price: 0,
    annualPrice: 0,
    features: [
      'Everything in Professional',
      'Additional orgs per environment',
      'Custom AI analysis profiles (Regulatory, compliance, and domain-specific)',
      'Volume pricing',
      'Dedicated account manager',
      'SLA guarantees',
      'Priority feature requests'
    ],
    limitations: [],
    ctaText: 'Contact Sales',
    ctaAction: 'contact',
    badge: 'Custom'
  }
};

function PricingCard({ tier, tierKey, onSelect, billingCycle, isSelected, isHovered, hasInteracted, onHover, onHoverEnd, hoveredCard }: { 
  tier: PricingTier; 
  tierKey: string;
  onSelect: () => void;
  billingCycle: 'monthly' | 'annual';
  isSelected: boolean;
  isHovered: boolean;
  hasInteracted: boolean;
  onHover: () => void;
  onHoverEnd: () => void;
  hoveredCard: string | null;
}) {
  const basePrice = billingCycle === 'annual' ? tier.annualPrice : tier.price;
  const displayPrice = basePrice;

  const isProfessional = tierKey === 'professional';
  const baseScale = isProfessional ? 'scale-100' : 'scale-90';
  
  // Show default hover state for Professional when no cards are hovered
  const noCardsHovered = !hasInteracted || (!isHovered && hoveredCard === null);
  const showDefaultHover = isProfessional && noCardsHovered;
  
  const getScale = () => {
    // If hovered, always show enlarged
    if (isHovered) return 'scale-105';
    // If showing default hover (Professional when no cards are hovered)
    if (showDefaultHover) return 'scale-105';
    // Otherwise, show base scale
    return baseScale;
  };

  const getRingColorClass = () => {
    if (tierKey === 'starter') return 'ring-[#FFBC06]';
    if (tierKey === 'standard') return 'ring-green-600';
    if (tierKey === 'enterprise') return 'ring-purple-600';
    return 'ring-blue-600';
  };

  const shouldShowRing = isHovered || showDefaultHover;

  return (
    <div 
      className={`relative rounded-lg shadow-lg bg-white p-8 transition-all duration-300 cursor-pointer ${
        shouldShowRing ? `ring-2 ${getRingColorClass()}` : ''
      } ${getScale()}`}
      onClick={onSelect}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
    >
      {tier.badge && (
        <div className={`absolute top-0 right-4 -translate-y-1/2 transition-opacity duration-300 ${
          (isHovered || showDefaultHover) ? 'opacity-100' : 'opacity-0'
        }`}>
          <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
            tierKey === 'starter' ? 'text-white' :
            tierKey === 'standard' ? 'bg-green-600 text-white' :
            tierKey === 'enterprise' ? 'bg-purple-600 text-white' :
            'bg-blue-600 text-white'
          }`} style={tierKey === 'starter' ? { backgroundColor: '#FFBC06' } : {}}>
            {tier.badge}
          </span>
        </div>
      )}
      
      <div>
        <h3 className="text-2xl font-bold text-gray-900">{tier.name}</h3>
        <div className="mt-4">
          {tier.price > 0 ? (
            <>
              <div className="flex items-baseline">
                {billingCycle === 'annual' ? (
                  <>
                    <span className="text-4xl font-bold text-gray-900">
                      ${tier.annualMonthlyEquivalent || Math.floor(tier.annualPrice / 12)}
                    </span>
                    <span className="ml-1 text-gray-500">/month</span>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-gray-900">
                      ${displayPrice}
                    </span>
                    <span className="ml-1 text-gray-500">/month</span>
                  </>
                )}
              </div>
              {billingCycle === 'annual' && (
                <div className="mt-1">
                  <div className="text-sm text-gray-600">
                    ${tier.annualPrice} billed annually
                  </div>
                  <div className="text-sm">
                    <span className="line-through text-gray-500">${tier.price}/month</span>
                    <span className="ml-2 text-green-600 font-medium">Save ${tier.price * 12 - tier.annualPrice}!</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-4xl font-bold text-gray-900">Contact Sales</div>
          )}
        </div>
        
        <ul className="mt-6 space-y-4">
          {tier.features.map((feature, index) => (
            <li key={index} className="flex">
              <CheckIcon className="flex-shrink-0 w-5 h-5 text-green-500" />
              <span className="ml-3 text-gray-700">{feature}</span>
            </li>
          ))}
          {tier.limitations.map((limitation, index) => (
            <li key={`limitation-${index}`} className="flex">
              <XMarkIcon className="flex-shrink-0 w-5 h-5 text-red-500" />
              <span className="ml-3 text-gray-500">{limitation}</span>
            </li>
          ))}
        </ul>
        
        <div className="mt-8">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            className={`w-full px-4 py-2 rounded-md font-medium transition-colors duration-300 ${
              (isHovered || showDefaultHover)
                ? tierKey === 'starter' ? 'text-white' :
                  tierKey === 'standard' ? 'bg-green-600 text-white hover:bg-green-700' :
                  tierKey === 'enterprise' ? 'bg-purple-600 text-white hover:bg-purple-700' :
                  'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
            style={(isHovered || showDefaultHover) && tierKey === 'starter' ? { backgroundColor: '#FFBC06' } : {}}
            onMouseEnter={(e) => {
              if (tierKey === 'starter' && (isHovered || showDefaultHover)) {
                e.currentTarget.style.backgroundColor = '#E5A905';
              }
            }}
            onMouseLeave={(e) => {
              if (tierKey === 'starter' && (isHovered || showDefaultHover)) {
                e.currentTarget.style.backgroundColor = '#FFBC06';
              }
            }}
          >
            {tier.ctaText}
          </button>
        </div>
      </div>
    </div>
  );
}

function calculateDiscountedPrice(price: number, coupon: any): number {
  if (coupon.type === 'fixed_amount') {
    return Math.max(0, price - coupon.amount);
  } else if (coupon.type === 'percentage') {
    return Math.round(price * (1 - coupon.percentage / 100));
  }
  return price;
}

function CheckoutForm({ selectedPlan, initialCouponCode, billingCycle, onSuccess, onBack }: {
  selectedPlan: string;
  initialCouponCode: string;
  billingCycle: 'monthly' | 'annual';
  onSuccess: () => void;
  onBack: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const { betaAccess } = useBetaAccess();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState(initialCouponCode);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [currentBillingCycle, setCurrentBillingCycle] = useState(billingCycle);
  const [betaCouponApplied, setBetaCouponApplied] = useState(false);

  const tier = pricingTiers[selectedPlan];
  const basePrice = currentBillingCycle === 'annual' ? tier.annualPrice : tier.price;
  const displayPrice = appliedCoupon && basePrice > 0 
    ? calculateDiscountedPrice(basePrice, appliedCoupon)
    : basePrice;
  
  // Calculate savings percentage for annual
  const annualSavings = tier.price * 12 - tier.annualPrice;
  const annualSavingsPercent = Math.round((annualSavings / (tier.price * 12)) * 100);

  // Auto-apply beta discount if user has beta access
  useEffect(() => {
    if (betaAccess.hasActiveBeta && betaAccess.discountPercentage && !betaCouponApplied) {
      // Set up the beta coupon automatically
      setAppliedCoupon({
        type: 'percentage',
        percentage: betaAccess.discountPercentage,
        duration: betaAccess.discountMonths,
        isBetaCoupon: true,
        source: 'beta_access'
      });
      setBetaCouponApplied(true);
      
      // Show message to user
      console.log('Beta discount automatically applied:', betaAccess.discountPercentage + '%');
    } else if (initialCouponCode && !appliedCoupon) {
      applyCoupon();
    }
  }, [betaAccess, initialCouponCode, betaCouponApplied]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-coupon', {
        body: { code: couponCode }
      });

      if (error) throw error;

      setAppliedCoupon(data);
    } catch (err) {
      setAppliedCoupon(null);
      setError('Invalid coupon code');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    
    // This should not happen as we redirect to signup for non-authenticated users
    if (!user) {
      setError('Please sign in to continue');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { data, error: apiError } = await supabase.functions.invoke('create-subscription', {
        body: {
          plan: selectedPlan,
          couponCode: appliedCoupon?.code || '',
          billingCycle: currentBillingCycle,
        }
      });

      if (apiError) {
        console.error('Create subscription error:', apiError);
        // Try to get more details from the error
        if (apiError instanceof Error && 'body' in apiError) {
          const errorBody = (apiError as any).body;
          console.error('Error details:', errorBody);
          if (errorBody?.error) {
            throw new Error(errorBody.error);
          }
        }
        throw apiError;
      }
      
      if (!data || !data.clientSecret) {
        console.error('Invalid response from create-subscription:', data);
        throw new Error('Invalid response from payment system');
      }

      let result;
      
      // Handle setup intent for trial subscriptions vs payment intent for immediate payment
      if (data.type === 'setup') {
        console.log('Confirming setup intent for trial subscription');
        result = await stripe.confirmCardSetup(data.clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement)!,
            billing_details: {
              email: user.email,
            },
          },
        });
      } else {
        console.log('Confirming payment intent');
        result = await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement)!,
            billing_details: {
              email: user.email,
            },
          },
        });
      }

      if (result.error) {
        setError(result.error.message || 'Payment failed');
      } else {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Payment processing error:', err);
      
      // Check if it's a response with error details
      if (err.response && err.response.data) {
        console.error('Error details:', err.response.data);
        setError(err.response.data.error || err.message || 'Something went wrong');
      } else {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Order Summary */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
        
        <div className="space-y-3">
          {/* Plan Details */}
          <div className="flex justify-between">
            <div>
              <p className="font-medium text-gray-900">{tier.name} Plan</p>
              <p className="text-sm text-gray-500">{currentBillingCycle === 'annual' ? 'Annual' : 'Monthly'} billing</p>
            </div>
            <p className="font-medium text-gray-900">
              ${currentBillingCycle === 'annual' ? tier.annualPrice : tier.price}
              <span className="text-sm text-gray-500">/{currentBillingCycle === 'annual' ? 'year' : 'month'}</span>
            </p>
          </div>

          {/* Coupon Code Input */}
          <div className="pt-3 border-t">
            {/* Show beta discount if auto-applied */}
            {appliedCoupon && appliedCoupon.isBetaCoupon ? (
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <p className="text-sm font-medium text-green-800">
                  Beta Participant Discount Applied! 🎉
                </p>
                <p className="text-sm text-green-600 mt-1">
                  {appliedCoupon.percentage}% off {appliedCoupon.duration && appliedCoupon.duration !== 999 ? `for ${appliedCoupon.duration} months` : 'your subscription'}
                </p>
              </div>
            ) : (
              <>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Coupon code"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 disabled:opacity-50"
                  >
                    Apply
                  </button>
                </div>
                {appliedCoupon && !appliedCoupon.isBetaCoupon && (
                  <p className="mt-2 text-sm text-green-600">
                    Coupon applied: {appliedCoupon.type === 'percentage' ? `${appliedCoupon.percentage}% off` : `$${appliedCoupon.amount} off`}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Annual Upgrade Option - Only show if they selected monthly */}
          {billingCycle === 'monthly' && (
            <div className="pt-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentBillingCycle === 'annual'}
                  onChange={(e) => setCurrentBillingCycle(e.target.checked ? 'annual' : 'monthly')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm">
                  <span className="font-medium text-gray-900">Switch to annual billing</span>
                  <span className="text-green-600 ml-1">
                    (Save {annualSavingsPercent}% - ${annualSavings}/year)
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Discount */}
          {appliedCoupon && displayPrice !== basePrice && (
            <div className="flex justify-between text-green-600">
              <p>Discount</p>
              <p>-${basePrice - displayPrice}</p>
            </div>
          )}

          {/* Total */}
          <div className="pt-3 border-t flex justify-between">
            <p className="font-semibold text-gray-900">Total</p>
            <div className="text-right">
              <p className="font-semibold text-gray-900">
                ${displayPrice}
                <span className="text-sm text-gray-500">/{currentBillingCycle === 'annual' ? 'year' : 'month'}</span>
              </p>
              {currentBillingCycle === 'annual' && (
                <p className="text-sm text-gray-500">${tier.annualMonthlyEquivalent || Math.floor(displayPrice / 12)}/month</p>
              )}
            </div>
          </div>

          {/* Trial Notice - Don't show for beta users */}
          {!betaAccess.hasActiveBeta && (
            <div className="pt-3 border-t">
              <p className="text-sm text-gray-600">
                <span className="font-medium">7-day free trial included.</span> You won't be charged until your trial ends.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Method
          </label>
          <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
            {!stripe ? (
              <div className="text-center py-4 text-gray-500">
                <p>Loading payment form...</p>
                <p className="text-xs mt-1">If this persists, please check your connection and refresh the page.</p>
              </div>
            ) : (
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#1f2937',
                      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                      '::placeholder': {
                        color: '#9ca3af',
                      },
                    },
                    invalid: {
                      color: '#ef4444',
                      iconColor: '#ef4444',
                    },
                  },
                  hidePostalCode: true,
                }}
              />
            )}
          </div>
        </div>
        
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!stripe || processing}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {processing ? 'Processing...' : betaAccess.hasActiveBeta ? 'Complete Purchase' : 'Start Free Trial'}
          </button>
        </div>
      </form>
    </div>
  );
}

// FAQ data
const faqs = [
  {
    question: "What is an \"Environment\"?",
    answer: "An environment is a Production Org + up to 4 related sandboxes. Our Professional plan provides 1 free environment, but you can add on additional environments as needed. Consider our Enterprise plan if you need more than 4 sandboxes in any one environment."
  },
  {
    question: "What happens after my free trial?",
    answer: "After your 7-day free trial, you'll be automatically charged for your chosen plan unless you cancel. You can cancel anytime during the trial period."
  },
  {
    question: "Can I change plans later?",
    answer: "Yes! Upgrade anytime and access new features immediately. We'll only charge you for the time remaining in your billing cycle. Downgrades take effect at your next renewal, so you can continue enjoying your current plan's features until then—no need to rush."
  },
  {
    question: "How many users can access my account?",
    answer: "All plans include unlimited users from your organization. Simply invite team members after you sign up."
  },
  {
    question: "Do I need to install anything in Salesforce?",
    answer: "No installation required! FlowDocs connects via Salesforce's secure OAuth - you just click to authorize and we handle the rest."
  }
];

export default function UpgradePage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { currentAccountId } = useWorkspace();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<string>('professional');
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Check for stored plan selection on mount
  useEffect(() => {
    if (user) {
      const storedPlan = sessionStorage.getItem('selectedPlan');
      const storedBillingCycle = sessionStorage.getItem('selectedBillingCycle');

      if (storedPlan) {
        // User has returned after signup/confirmation with a plan selected
        setSelectedPlan(storedPlan);
        if (storedBillingCycle) {
          setBillingCycle(storedBillingCycle as 'monthly' | 'annual');
        }
        setShowCheckout(true);

        // Clear from sessionStorage so it doesn't persist
        sessionStorage.removeItem('selectedPlan');
        sessionStorage.removeItem('selectedBillingCycle');
      }
    }
  }, [user]);

  const handlePlanSelect = (planName: string) => {
    if (planName === 'enterprise') {
      window.location.href = 'mailto:sales@getflowdocs.com?subject=Enterprise Plan Inquiry';
      return;
    }

    // If user is not authenticated, store selection and redirect to sign up
    if (!user) {
      sessionStorage.setItem('selectedPlan', planName);
      sessionStorage.setItem('selectedBillingCycle', billingCycle);
      navigate('/signup');
      return;
    }

    setSelectedPlan(planName);
    setShowCheckout(true);
  };


  const handleCheckoutSuccess = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-black" style={{ background: 'linear-gradient(167deg, #0f172a, #1a2c6c, #000000)' }}>
      <Navbar onSignOut={signOut} isPublic={!user} />
      
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {!showCheckout ? (
              <>
                <h1 className="text-5xl font-bold text-white">
                  Choose Your Plan
                </h1>
                <p className="mt-4 text-lg text-gray-300">
                  Select the perfect plan for your Salesforce documentation needs
                </p>
                <p className="mt-2 text-sm text-gray-400">
                  Need custom pricing or enterprise features? Check out our Enterprise plan or{' '}
                  <a
                    href="mailto:sales@getflowdocs.com?subject=Enterprise Plan Inquiry"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    contact sales
                  </a>
                </p>
              </>
            ) : (
              <>
                <h1 className="text-5xl font-bold text-white">
                  Complete Your Order
                </h1>
                <p className="mt-4 text-lg text-gray-300">
                  {selectedPlan && pricingTiers[selectedPlan] ?
                    `You're upgrading to the ${pricingTiers[selectedPlan].name} Plan` :
                    'Finalize your subscription details below'}
                </p>
              </>
            )}

            {/* Billing Cycle Toggle - Only show when not in checkout */}
            {!showCheckout && (
              <div className="mt-8 flex items-center justify-center">
                <span className={`mr-3 ${billingCycle === 'monthly' ? 'text-white font-medium' : 'text-gray-400'}`}>
                  Monthly
                </span>
                <button
                  type="button"
                  className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 bg-blue-600"
                  role="switch"
                  aria-checked={billingCycle === 'annual'}
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      billingCycle === 'annual' ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className={`ml-3 ${billingCycle === 'annual' ? 'text-white font-medium' : 'text-gray-400'}`}>
                  Annual
                  <span className="ml-1 text-green-600 text-sm font-medium">(2 months FREE!)</span>
                </span>
              </div>
            )}
          </div>

          {!showCheckout ? (
            <div className="mt-12 grid gap-8 lg:grid-cols-3 items-center max-w-6xl mx-auto">
              <PricingCard
                tier={pricingTiers.starter}
                tierKey="starter"
                onSelect={() => {
                  setSelectedCard('starter');
                  handlePlanSelect('starter');
                }}
                billingCycle={billingCycle}
                isSelected={selectedCard === 'starter'}
                isHovered={hoveredCard === 'starter'}
                hasInteracted={hasInteracted}
                onHover={() => {
                  setHoveredCard('starter');
                  setHasInteracted(true);
                }}
                onHoverEnd={() => setHoveredCard(null)}
                hoveredCard={hoveredCard}
              />
              <PricingCard
                tier={pricingTiers.professional}
                tierKey="professional"
                onSelect={() => {
                  setSelectedCard('professional');
                  handlePlanSelect('professional');
                }}
                billingCycle={billingCycle}
                isSelected={selectedCard === 'professional'}
                isHovered={hoveredCard === 'professional'}
                hasInteracted={hasInteracted}
                onHover={() => {
                  setHoveredCard('professional');
                  setHasInteracted(true);
                }}
                onHoverEnd={() => setHoveredCard(null)}
                hoveredCard={hoveredCard}
              />
              <PricingCard
                tier={pricingTiers.enterprise}
                tierKey="enterprise"
                onSelect={() => {
                  setSelectedCard('enterprise');
                  handlePlanSelect('enterprise');
                }}
                billingCycle={billingCycle}
                isSelected={selectedCard === 'enterprise'}
                isHovered={hoveredCard === 'enterprise'}
                hasInteracted={hasInteracted}
                onHover={() => {
                  setHoveredCard('enterprise');
                  setHasInteracted(true);
                }}
                onHoverEnd={() => setHoveredCard(null)}
                hoveredCard={hoveredCard}
              />
            </div>
          ) : (
            <div className="mt-12 max-w-lg mx-auto">
              <div className="bg-white shadow-lg rounded-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Complete Your Order
                </h2>
                <Elements stripe={stripePromise}>
                  <CheckoutForm
                    selectedPlan={selectedPlan!}
                    initialCouponCode={couponCode}
                    billingCycle={billingCycle}
                    onSuccess={handleCheckoutSuccess}
                    onBack={() => setShowCheckout(false)}
                  />
                </Elements>
              </div>
            </div>
          )}

          {/* Plan Comparison Section - Only show when not in checkout */}
          {!showCheckout && (
            <div className="mt-20 lg:w-2/3 mx-auto">
              <div className="bg-white rounded-lg shadow-xl p-8">
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                  Compare Plans
                </h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-4 px-4 font-medium text-gray-900">Features</th>
                      <th className="text-center py-4 px-4">
                        <div className="font-medium text-gray-900">Starter</div>
                        <div className="text-sm text-gray-500">${billingCycle === 'annual' ? '83' : '99'}/mo</div>
                      </th>
                      <th className="text-center py-4 px-4">
                        <div className="font-medium text-gray-900">Professional</div>
                        <div className="text-sm text-gray-500">${billingCycle === 'annual' ? '249' : '299'}/mo</div>
                      </th>
                      <th className="text-center py-4 px-4">
                        <div className="font-medium text-gray-900">Enterprise</div>
                        <div className="text-sm text-gray-500">Custom</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="py-4 px-4 text-gray-700">Flow Health Dashboard</td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">Performance Scorecards</td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">AI Summaries</td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">Field Impact Analysis</td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">Entry Criteria Map</td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">Orgs/Environments</td>
                      <td className="text-center py-4 px-4">1 org</td>
                      <td className="text-center py-4 px-4">
                        <div>1 environment</div>
                        <div className="text-sm text-gray-500">(Prod + 4 sandboxes)</div>
                        <div className="text-sm text-gray-500">Add-ons available</div>
                      </td>
                      <td className="text-center py-4 px-4">Custom</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">Team Members</td>
                      <td className="text-center py-4 px-4">Unlimited</td>
                      <td className="text-center py-4 px-4">Unlimited</td>
                      <td className="text-center py-4 px-4">Unlimited</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">Flow Sync Frequency</td>
                      <td className="text-center py-4 px-4">Daily</td>
                      <td className="text-center py-4 px-4">Daily + On-demand</td>
                      <td className="text-center py-4 px-4">Configurable</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">PDF Export</td>
                      <td className="text-center py-4 px-4 text-gray-400">—</td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                      <td className="text-center py-4 px-4">
                        <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">AI Analysis Profiles</td>
                      <td className="text-center py-4 px-4">Performance</td>
                      <td className="text-center py-4 px-4">Performance</td>
                      <td className="text-center py-4 px-4">
                        <div>Custom analysis profiles</div>
                        <div className="text-sm text-gray-500">(E.g., industry & compliance)</div>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-gray-700">Support</td>
                      <td className="text-center py-4 px-4">Standard</td>
                      <td className="text-center py-4 px-4">Priority</td>
                      <td className="text-center py-4 px-4">Dedicated</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}

          {/* FAQ Section - Only show when not in checkout */}
          {!showCheckout && (
            <div className="mt-20">
              <h2 className="text-3xl font-bold text-center text-white mb-12">
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-4 max-w-3xl mx-auto">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-white bg-opacity-10 backdrop-blur-md rounded-lg">
                    <button
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white hover:bg-opacity-5 transition-colors rounded-lg"
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    >
                      <span className="font-medium text-white">{faq.question}</span>
                      <QuestionMarkCircleIcon 
                        className={`w-5 h-5 text-gray-300 transform transition-transform ${
                          expandedFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-300">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}